import { useState } from 'react'
import { Mail, MapPin, Send, CheckCircle2 } from 'lucide-react'
import { brand, contact } from '../data/site'
import Reveal from './Reveal'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function Contact() {
  const [values, setValues] = useState({ name: '', email: '', company: '', message: '' })
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)

  function validate(v) {
    const e = {}
    if (!v.name.trim()) e.name = 'Please enter your name.'
    if (!v.email.trim()) e.email = 'Please enter your email.'
    else if (!EMAIL_RE.test(v.email)) e.email = 'Please enter a valid email address.'
    if (!v.message.trim()) e.message = 'Please tell us a little about your project.'
    return e
  }

  function handleChange(e) {
    const { name, value } = e.target
    setValues((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    const nextErrors = validate(values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) {
      // Front-end demo only — no data leaves the browser.
      setSubmitted(true)
    }
  }

  const details = [
    { icon: Mail, label: brand.email, href: `mailto:${brand.email}` },
    {
      icon: MapPin,
      label: brand.location,
      href: `https://maps.google.com/?q=${encodeURIComponent(brand.location)}`,
    },
  ]

  const inputBase =
    'w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:ring-2 focus:ring-brand-500/30'

  return (
    <section id="contact" className="py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="overflow-hidden rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-600 to-brand-800 shadow-[var(--shadow-glow)]">
          <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-2 lg:gap-16">
            {/* left: pitch + details */}
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-wider text-brand-200">
                {contact.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {contact.title}
              </h2>
              <p className="mt-4 max-w-md text-lg leading-relaxed text-brand-100">
                {contact.subtitle}
              </p>

              <ul className="mt-8 space-y-4">
                {details.map((d) => {
                  const Icon = d.icon
                  const inner = (
                    <span className="flex items-center gap-3 text-brand-50">
                      <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-sm font-medium">{d.label}</span>
                    </span>
                  )
                  return (
                    <li key={d.label}>
                      {d.href ? (
                        <a href={d.href} className="transition-opacity hover:opacity-80">
                          {inner}
                        </a>
                      ) : (
                        inner
                      )}
                    </li>
                  )
                })}
              </ul>
            </Reveal>

            {/* right: form / success */}
            <Reveal delay={100} className="rounded-2xl bg-white p-6 shadow-[var(--shadow-lift)] sm:p-8">
              {submitted ? (
                <div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-brand-50 text-brand-600">
                    <CheckCircle2 className="h-9 w-9" />
                  </span>
                  <h3 className="mt-5 text-xl font-semibold text-slate-900">Thank you, {values.name.split(' ')[0]}!</h3>
                  <p className="mt-2 max-w-xs text-sm text-slate-500">
                    Your message has been received. Our team will get back to you within one business day.
                  </p>
                </div>
              ) : (
                <form noValidate onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={values.name}
                      onChange={handleChange}
                      placeholder="Jane Doe"
                      className={`${inputBase} ${errors.name ? 'border-red-400 focus:ring-red-200' : 'border-slate-200 focus:border-brand-500'}`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        placeholder="jane@company.com"
                        className={`${inputBase} ${errors.email ? 'border-red-400 focus:ring-red-200' : 'border-slate-200 focus:border-brand-500'}`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-slate-700">
                        Company <span className="text-slate-400">(optional)</span>
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={values.company}
                        onChange={handleChange}
                        placeholder="Acme Inc."
                        className={`${inputBase} border-slate-200 focus:border-brand-500`}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Project details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={values.message}
                      onChange={handleChange}
                      placeholder="Tell us what you're building and how we can help…"
                      className={`${inputBase} resize-none ${errors.message ? 'border-red-400 focus:ring-red-200' : 'border-slate-200 focus:border-brand-500'}`}
                    />
                    {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-3.5 text-sm font-semibold text-white shadow-[var(--shadow-lift)] transition-all hover:bg-brand-700"
                  >
                    Send message
                    <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}
